// src/views/Inventory/StockOpname/StockOpnameForm.jsx
import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Row,
  Col,
  Input,
  DatePicker,
  Select,
  Button,
  Table,
  Space,
  InputNumber,
  Popconfirm,
  message,
  Tag,
  Modal,
} from "antd";
import {
  SaveOutlined,
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import axiosInstance from "../../../configs"; // SESUAIKAN PATH

const { TextArea } = Input;
const { Option } = Select;

export default function StockOpnameForm() {
  const [form] = Form.useForm();

  const { id } = useParams(); // id_stock_opname saat edit/detail
  const location = useLocation();

  const isAddMode = location.pathname.includes("/add");
  const isDetailMode = location.pathname.includes("/detail");
  const isEditMode = location.pathname.includes("/edit");

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [detailRows, setDetailRows] = useState([]);
  const [status, setStatus] = useState(0);

  // Confirmation Modal State
  const [confirmVisible, setConfirmVisible] = useState(false);
  const [pendingValues, setPendingValues] = useState(null);

  // master data
  const [itemList, setItemList] = useState([]);

  // ====== FETCH MASTER ======

  const fetchItems = async () => {
    try {
      const res = await axiosInstance.get("/item", {
        params: { limit: 1000, page: 1 },
      });
      setItemList(
        (res.data.data || []).map((it) => ({
          label: `${it.item_code} - ${it.item_name}`,
          value: it.item_code,
        }))
      );
    } catch (err) {
      console.error("Failed fetch items", err);
    }
  };

  // ====== FETCH DATA SAAT EDIT/DETAIL ======
  const fetchDetail = async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await axiosInstance.get(`/stock-opname/${id}/with-details`);
      const { header, details } = res.data;

      setStatus(header.status);

      form.setFieldsValue({
        opname_number: header.opname_number,
        opname_date: header.opname_date ? dayjs(header.opname_date) : null,
        id_warehouse: header.id_warehouse,
        id_customer: header.id_customer,
        notes: header.notes,
      });

      setDetailRows(
        (details || []).map((d) => ({
          key: d.id_stock_opname_detail,
          ...d,
        }))
      );
    } catch (err) {
      console.error("Failed fetch stock opname detail", err);
      message.error("Gagal mengambil data stock opname");
    } finally {
      setLoading(false);
    }
  };

  // ====== FETCH SYSTEM STOCK (AUTO POPULATE) ======
  const fetchSystemStock = async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/monitoring-stock");
      const customers = res.data || [];

      const newRows = [];
      customers.forEach((c) => {
        (c.items || []).forEach((item) => {
          (item.docs || []).forEach((doc) => {
            // Filter only items with stock > 0 if needed, or keep all
            if (doc.total_ct > 0) {
              newRows.push({
                key: `${item.item_code}-${doc.lokasi_lot}-${Date.now()}-${Math.random()}`,
                item_code: item.item_code,
                lokasi_lot: doc.lokasi_lot || "",
                satuan: doc.satuan_kemasan || item.satuan_kemasan || "KEMASAN",
                qty_system: doc.total_ct,
                qty_physical: doc.total_ct, // Default to system qty (editable)
                qty_diff: 0, // Initially no difference
                notes: "SESUAI", // Default keterangan when qty matches
              });
            }
          });
        });
      });

      setDetailRows(newRows);
    } catch (err) {
      console.error("Failed fetch system stock", err);
      message.error("Gagal mengambil data stok sistem");
    } finally {
      setLoading(false);
    }
  };

  const fetchNextNumber = async () => {
    try {
      const res = await axiosInstance.get("/stock-opname/next-number");
      if (res.data && res.data.opname_number) {
        form.setFieldsValue({ opname_number: res.data.opname_number });
      }
    } catch (err) {
      console.error("Failed fetch next number", err);
    }
  };

  useEffect(() => {
    fetchItems();
    if (isAddMode) {
      fetchSystemStock();
      fetchNextNumber();
    } else {
      fetchDetail();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAddMode]);

  // ====== HANDLE DETAIL ROWS ======
  const addDetailRow = () => {
    const newRow = {
      key: Date.now(),
      item_code: null,
      lokasi_lot: form.getFieldValue("id_warehouse") || null,
      satuan: "",
      qty_system: 0,
      qty_physical: 0,
      qty_diff: 0,
      notes: "",
    };
    setDetailRows((prev) => [...prev, newRow]);
  };

  const removeDetailRow = (key) => {
    setDetailRows((prev) => prev.filter((row) => row.key !== key));
  };

  const updateDetailRow = (key, field, value) => {
    setDetailRows((prev) =>
      prev.map((row) => {
        if (row.key !== key) return row;
        const updated = { ...row, [field]: value };

        // auto hitung qty_diff kalau ubah qty_system / qty_physical
        if (field === "qty_system" || field === "qty_physical") {
          const sys = Number(
            field === "qty_system" ? value : updated.qty_system || 0
          );
          const phy = Number(
            field === "qty_physical" ? value : updated.qty_physical || 0
          );
          updated.qty_diff = phy - sys;

          // Auto-fill keterangan based on difference
          if (updated.qty_diff === 0) {
            updated.notes = "SESUAI";
          } else if (updated.qty_diff < 0) {
            updated.notes = "KURANG";
          } else {
            updated.notes = "LEBIH";
          }
        }

        return updated;
      })
    );
  };

  const columnsDetail = [
    {
      title: "No.",
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: "Item",
      dataIndex: "item_code",
      width: 240,
      render: (value, record) =>
        isDetailMode ? (
          <span>{value}</span>
        ) : (
          <Select
            showSearch
            allowClear
            placeholder="Pilih barang"
            size="small"
            style={{ width: "100%" }}
            value={value}
            onChange={(val) => updateDetailRow(record.key, "item_code", val)}
            optionFilterProp="label"
          >
            {itemList.map((it) => (
              <Option key={it.value} value={it.value} label={it.label}>
                {it.label}
              </Option>
            ))}
          </Select>
        ),
    },
    {
      title: "GC / Lot",
      dataIndex: "lokasi_lot",
      width: 130,
      render: (value, record) =>
        isDetailMode ? (
          value
        ) : (
          <Input
            size="small"
            value={value}
            onChange={(e) => updateDetailRow(record.key, "lokasi_lot", e.target.value)}
          />
        ),
    },
    {
      title: "Satuan",
      dataIndex: "satuan",
      width: 100,
      render: (value, record) =>
        isDetailMode ? (
          value
        ) : (
          <Input
            size="small"
            value={value}
            onChange={(e) =>
              updateDetailRow(record.key, "satuan", e.target.value)
            }
          />
        ),
    },
    {
      title: "Qty System",
      dataIndex: "qty_system",
      width: 110,
      align: "right",
      render: (value, record) =>
        isDetailMode ? (
          value
        ) : (
          <InputNumber
            size="small"
            min={0}
            style={{ width: "100%" }}
            value={value}
            onChange={(val) =>
              updateDetailRow(record.key, "qty_system", val || 0)
            }
          />
        ),
    },
    {
      title: "Qty Fisik",
      dataIndex: "qty_physical",
      width: 110,
      align: "right",
      render: (value, record) =>
        isDetailMode ? (
          value
        ) : (
          <InputNumber
            size="small"
            min={0}
            style={{ width: "100%" }}
            value={value}
            onChange={(val) =>
              updateDetailRow(record.key, "qty_physical", val || 0)
            }
          />
        ),
    },
    {
      title: "Selisih",
      dataIndex: "qty_diff",
      width: 110,
      align: "right",
      render: (value) => {
        const num = Number(value || 0);
        const color = num === 0 ? "default" : num > 0 ? "green" : "red";
        const label =
          num === 0 ? "0" : num > 0 ? `+${num.toFixed(2)}` : num.toFixed(2);
        return <Tag color={color}>{label}</Tag>;
      },
    },
    {
      title: "Keterangan",
      dataIndex: "notes",
      width: 200,
      render: (value, record) =>
        isDetailMode ? (
          value
        ) : (
          <Input
            size="small"
            value={value}
            onChange={(e) =>
              updateDetailRow(record.key, "notes", e.target.value)
            }
          />
        ),
    },
    !isDetailMode && {
      title: "",
      key: "action",
      width: 60,
      align: "center",
      render: (_, record) => (
        <Popconfirm
          title="Hapus baris?"
          onConfirm={() => removeDetailRow(record.key)}
        >
          <Button danger icon={<DeleteOutlined />} size="small" type="text" />
        </Popconfirm>
      ),
    },
  ].filter(Boolean);

  const handleSubmit = (values) => {
    if (detailRows.length === 0) {
      message.warning("Detail barang minimal 1 baris");
      return;
    }

    setPendingValues(values);
    setConfirmVisible(true);
  };

  const handleConfirmSubmit = async () => {
    const values = pendingValues;

    try {
      setSaving(true);

      const payload = {
        header: {
          opname_number: values.opname_number,
          opname_date: values.opname_date
            ? values.opname_date.format("YYYY-MM-DD")
            : null,
          id_warehouse: values.id_warehouse,
          status: status, // biasanya 0 (draft)
          notes: values.notes || null,
        },
        details: detailRows.map((row) => ({
          id_stock_opname_detail: row.id_stock_opname_detail || undefined, // untuk edit
          item_code: row.item_code,
          lokasi_lot: row.lokasi_lot || "",
          satuan: row.satuan || "",
          qty_system: Number(row.qty_system || 0),
          qty_physical: Number(row.qty_physical || 0),
          qty_diff:
            row.qty_diff != null
              ? Number(row.qty_diff)
              : Number(row.qty_physical || 0) - Number(row.qty_system || 0),
          notes: row.notes || null,
        })),
        deleted_detail_ids: [], // kalau mau implement delete hard dari server, tambahkan di sini
      };

      if (isAddMode) {
        const res = await axiosInstance.post("/stock-opname", payload);
        message.success("Stock opname berhasil dibuat");

        window.location.hash = `/inventory/stock-opname/edit/${res.data.id_stock_opname}`;
      } else if (isEditMode) {
        await axiosInstance.put(`/stock-opname/${id}/with-details`, payload);
        message.success("Stock opname berhasil disimpan");
        fetchDetail(); // refresh
      }
    } catch (err) {
      console.error("Failed save stock opname", err);
      const msg =
        err?.response?.data?.error ||
        "Gagal menyimpan stock opname, cek log server";
      message.error(msg);
    } finally {
      setSaving(false);
      setConfirmVisible(false);
    }
  };

  return (
    <div className="py-4">
      <Card
        title={
          <Space>
            <span style={{ fontWeight: 600 }}>
              {isAddMode
                ? "Tambah Stock Opname"
                : isDetailMode
                  ? "Detail Stock Opname"
                  : "Edit Stock Opname"}
            </span>
            {!isAddMode && (
              <Tag color={status === 1 ? "green" : "gold"}>
                {status === 1 ? "Final" : "Draft"}
              </Tag>
            )}
          </Space>
        }
        bodyStyle={{ padding: 16 }}
      >
        <Form
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          initialValues={{
            status: 0,
          }}
        >
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="No. Opname"
                name="opname_number"
              // rules={[{ required: true, message: "No. Opname wajib diisi" }]} 
              >
                <Input
                  placeholder="AUTO GEN"
                  disabled
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Tgl Opname"
                name="opname_date"
                rules={[
                  { required: true, message: "Tanggal opname wajib diisi" },
                ]}
              >
                <DatePicker
                  style={{ width: "100%" }}
                  format="YYYY-MM-DD"
                  disabled={isDetailMode}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label="Catatan" name="notes">
            <TextArea rows={3} disabled={isDetailMode} />
          </Form.Item>

          <Card
            size="small"
            title={
              <Space>
                <span style={{ fontWeight: 500 }}>Detail Barang</span>
                <Tag color="blue">Total baris: {detailRows.length || 0}</Tag>
              </Space>
            }
            extra={
              !isDetailMode && (
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addDetailRow}
                >
                  Tambah Baris
                </Button>
              )
            }
            bodyStyle={{ padding: 8 }}
          >
            <Table
              rowKey="key"
              columns={columnsDetail}
              dataSource={detailRows}
              pagination={false}
              size="small"
              scroll={{ x: 900 }}
            />
          </Card>

          {!isDetailMode && (
            <div
              style={{
                marginTop: 16,
                display: "flex",
                justifyContent: "flex-end",
                gap: 8,
              }}
            >
              <Button onClick={() => "/inventory/stock-opname"}>Batal</Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                htmlType="submit"
                loading={saving}
                style={{ background: "#3699FF", borderColor: "#3699FF" }}
              >
                Simpan
              </Button>
            </div>
          )}
        </Form>

      </Card>

      {/* Confirmation Modal */}
      <Modal
        title="Konfirmasi Stock Opname"
        open={confirmVisible}
        onOk={handleConfirmSubmit}
        onCancel={() => setConfirmVisible(false)}
        okText="Ya, Simpan"
        cancelText="Batal"
        confirmLoading={saving}
        width={600}
      >
        {pendingValues && (
          <div>
            <p>Apakah Anda yakin ingin menyimpan data stock opname ini?</p>
            <div style={{ background: "#f5f5f5", padding: 10, borderRadius: 4, marginBottom: 16 }}>
              <p style={{ margin: 0 }}>
                <strong>No Opname:</strong> {pendingValues.opname_number}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Tanggal:</strong>{" "}
                {pendingValues.opname_date
                  ? pendingValues.opname_date.format("DD MMMM YYYY")
                  : "-"}
              </p>
            </div>

            <div style={{ marginTop: 16 }}>
              <strong>Detail Barang:</strong>
              <div style={{ maxHeight: 200, overflowY: "auto", marginTop: 8, border: "1px solid #f0f0f0" }}>
                <table style={{ width: "100%", fontSize: 12, borderCollapse: "collapse" }}>
                  <thead style={{ background: "#fafafa", position: "sticky", top: 0 }}>
                    <tr>
                      <th style={{ padding: "4px 8px", textAlign: "left", borderBottom: "1px solid #f0f0f0" }}>Item Code</th>
                      <th style={{ padding: "4px 8px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>Qty System</th>
                      <th style={{ padding: "4px 8px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>Qty Fisik</th>
                      <th style={{ padding: "4px 8px", textAlign: "right", borderBottom: "1px solid #f0f0f0" }}>Selisih</th>
                    </tr>
                  </thead>
                  <tbody>
                    {detailRows.map((row, idx) => (
                      <tr key={idx} style={{ borderBottom: "1px solid #f0f0f0" }}>
                        <td style={{ padding: "4px 8px" }}>{row.item_code}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>{row.qty_system}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right" }}>{row.qty_physical}</td>
                        <td style={{ padding: "4px 8px", textAlign: "right", color: row.qty_diff !== 0 ? "red" : "inherit" }}>
                          {row.qty_diff}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div >
  );
}
